import {
  createFileRoute,
  Outlet,
  redirect,
  useBlocker,
  useNavigate
} from "@tanstack/react-router";
import { DateTime, Duration } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip
} from "@mantine/core";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RichTextEditor } from "@mantine/tiptap";
import { RecordingContext } from "./-RecordingContext";
import type { Recording, RecordingInList } from "@/models";
import { TranscriptionStatusMap } from "@/models/Recording";
import { RoutedTabs } from "@/components/RoutedTabs";
import { DataDisplay } from "@/components/DataDisplay";
import { getRecordingDetailsQuery } from "@/api/recordings/getRecordingDetails";
import {
  StreamingAudioPlayer,
  type StreamingAudioPlayerRef
} from "@/components/StreamingAudioPlayer";
import LoadingPane from "@/components/LoadingPane";
import {
  canAddOrEditRecordings,
  canEditLivingStory,
  canViewRecordings
} from "@/security/logic";
import {
  type UpdateNotesPayload,
  type UpdateNotesResponse,
  updateRecordingNotesMutation
} from "@/api/recordings/updateRecordingNotes";
import { useApi } from "@/api";
import { showError, showSuccess } from "@/notifications/Notifications";
import { UnsavedChangesModal } from "@/components/UnsavedChangesModal";
import { computeStatus } from "@/hooks/useRecordingsWithLocalSessions.logic";

function titleOrDate(recordingDetails?: Recording): string {
  if (!recordingDetails) {
    return "";
  }
  if (
    recordingDetails.recordingTitle &&
    recordingDetails.recordingTitle.trim() !== ""
  ) {
    return recordingDetails.recordingTitle;
  } else {
    return DateTime.fromISO(recordingDetails.startedDate).toFormat(
      "yyyy-MM-dd"
    );
  }
}

export const Route = createFileRoute(
  "/prospects/$prospectId/recordings/$recordingId"
)({
  component: RouteComponent,
  beforeLoad: ({ context, location }) => {
    const { userClaim, prospect } = context;

    // redirect from all $recordingId routes if user lacks view permissions
    if (prospect && !canViewRecordings(userClaim, prospect.communityId)) {
      throw redirect({
        to: "/notAuthorized/$",
        params: { _splat: location.href }
      });
    }
  }
});

function RouteComponent() {
  const { prospect, prospectName, userClaim } = Route.useRouteContext();
  const { prospectId, recordingId } = Route.useParams();
  const navigate = useNavigate();
  const { secureAxios } = useApi();
  const queryClient = useQueryClient();
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  /** Set in the discard of UnsavedChangesModal so that useBlocker won't prevent 
   navigation on a browser level when the user has already chosen to discard 
   (this is because closing the recording details modal takes the $recordingId 
   off of the route path, which is a browser navigation it would prompt about) */
  const isDiscardUnsavedChangesConfirmed = useRef(false);

  /** need this in order to not block navigation between child tabs (summary, 
   transcript) in useBlocker, since they don't result in data loss on this page 
   (isDirty only acts on the notes RTE which stays undisturbed on the page) */
  const { pathname: currentRoutePathname } = Route.useMatch();

  // Prevent accidental navigation away when the form is dirty
  useBlocker({
    shouldBlockFn: ({ next }) =>
      !isDiscardUnsavedChangesConfirmed.current &&
      isDirty &&
      !next.pathname.startsWith(currentRoutePathname) &&
      !window.confirm(
        "You have unsaved changes. Are you sure you want to leave?"
      )
  });

  // Live query so the details page updates as server-side processing progresses
  const { data: recordingDetails, isError: isRecordingDetailsError } = useQuery(
    {
      ...getRecordingDetailsQuery(secureAxios, recordingId),
      refetchOnWindowFocus: false
    }
  );

  // Auto-refresh while the recording is still being processed server-side
  const needsPolling =
    recordingDetails?.isSynchronized &&
    !recordingDetails.isSummarized &&
    recordingDetails.transcriptionStatus !== TranscriptionStatusMap.Failed;

  const refetchRef = useRef(() =>
    queryClient.invalidateQueries({
      queryKey: ["recording", "session", recordingId]
    })
  );

  useEffect(() => {
    if (!needsPolling || isRecordingDetailsError) {
      return;
    }
    const interval = setInterval(() => {
      void refetchRef.current();
    }, 15_000);
    return () => clearInterval(interval);
  }, [needsPolling, isRecordingDetailsError]);

  const recordingSyncStatus = recordingDetails
    ? computeStatus(recordingDetails)
    : undefined;

  const streamingAudioPlayerRef = useRef<StreamingAudioPlayerRef>(null);

  const hasNotesPermission = canAddOrEditRecordings(
    userClaim,
    prospect?.communityId ?? 0
  );

  const hasEditLivingStoryPermission = canEditLivingStory(
    userClaim,
    prospect?.communityId ?? 0
  );

  const [includeNotesInLivingStory, setIncludeNotesInLivingStory] =
    useState(false);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [editorInitialized, setEditorInitialized] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit, Highlight],
    content: "",
    onUpdate: ({ editor: updatedEditor }) => {
      setIsDirty(true);
      setIsEditorEmpty(updatedEditor.isEmpty);
    }
  });

  // Initialize editor content and related state when recording details first load
  useEffect(() => {
    if (recordingDetails && !editorInitialized) {
      editor.commands.setContent(recordingDetails.notesHtml ?? "");
      setIncludeNotesInLivingStory(recordingDetails.includeNotesInLivingStory);
      // (reset the dirty flag though, since that wasn't a real update from the user)
      setIsDirty(false);
      setIsEditorEmpty(!recordingDetails.notesHtml);
      setEditorInitialized(true);
    }
  }, [recordingDetails, editor, editorInitialized]);

  function handleCloseAttempt(): void {
    if (isDirty) {
      setShowUnsavedChangesModal(true);
    } else {
      handleClose();
    }
  }

  const updateNotesMutation = useMutation<
    UpdateNotesResponse,
    Error,
    UpdateNotesPayload
  >({
    ...updateRecordingNotesMutation(secureAxios, recordingId),
    onSuccess: (response) => {
      // Update the recording session in the query cache with the confirmed saved values
      queryClient.setQueryData<Recording | undefined>(
        ["recording", "session", recordingId],
        (old) =>
          old
            ? {
                ...old,
                notesHtml: response.notesHtml ?? undefined,
                includeNotesInLivingStory: response.includeNotesInLivingStory
              }
            : old
      );
      // Update it in the list of prospect's recordings too so the list isn't stale either
      queryClient.setQueryData<RecordingInList[]>(
        ["recordings", "prospect", Number(prospectId)],
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          return oldData.map((recording) =>
            recording.id === recordingId
              ? {
                  ...recording,
                  hasNotes: !!response.notesHtml,
                  includeNotesInLivingStory: response.includeNotesInLivingStory
                }
              : recording
          );
        }
      );
      showSuccess("Notes saved successfully", "Saved");
      setIsDirty(false);
    },
    onError: (error) => {
      showError("Failed to update notes. Please try again.");
      console.error("Failed to update notes:", error);
    }
  });

  const handleSaveNotes = useCallback(() => {
    const notesHtml = editor.isEmpty ? null : editor.getHTML();
    updateNotesMutation.mutate({
      notesHtml,
      includeNotesInLivingStory
    });
  }, [editor, includeNotesInLivingStory, updateNotesMutation]);

  const handleSeekToTime = useCallback((timeInSeconds: number) => {
    streamingAudioPlayerRef.current?.seekTo(timeInSeconds);
  }, []);
  const recordingContext = useMemo(
    () => ({ handleSeekToTime }),
    [handleSeekToTime]
  );

  const handleClose = useCallback(() => {
    void navigate({
      to: "/prospects/$prospectId/recordings",
      params: { prospectId }
    });
  }, [navigate, prospectId]);

  return (
    <>
      <Modal
        opened={true}
        closeOnClickOutside={false}
        closeOnEscape={false}
        onClose={handleCloseAttempt}
        title={titleOrDate(recordingDetails)}
        size={1150}
        centered
        styles={{ body: { overflow: "hidden" } }}
      >
        {!prospect || !recordingDetails ? (
          <LoadingPane />
        ) : (
          <Grid>
            <Grid.Col span={5}>
              <Paper
                shadow="xs"
                withBorder
                radius="md"
                bg="sparkYellow.10"
                bd="1px solid sparkYellow.11"
                pb={10}
                px={{ base: 15, lg: 25 }}
              >
                <Stack>
                  <Grid mt={10} mb={10}>
                    <Grid.Col span={6}>
                      <DataDisplay label="PROSPECT NAME" value={prospectName} />
                    </Grid.Col>
                    <Grid.Col span={3} visibleFrom="lg">
                      <DataDisplay
                        label="DURATION"
                        value={Duration.fromObject({
                          milliseconds: recordingDetails.durationMs
                        }).toFormat("h:mm:ss")}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 6, lg: 3 }}>
                      <DataDisplay
                        label="CREATED"
                        value={DateTime.fromISO(
                          recordingDetails.startedDate
                        ).toFormat("yyyy-MM-dd")}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <DataDisplay
                        label="COMMUNITY"
                        value={prospect.communityName}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <DataDisplay
                        label="RECORDING TYPE"
                        value={recordingDetails.recordingType?.name ?? ""}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <DataDisplay
                        label="SYNC STATUS"
                        value={recordingSyncStatus ?? ""}
                        textColor={
                          recordingSyncStatus === "Error Transcribing"
                            ? "red"
                            : undefined
                        }
                      />
                    </Grid.Col>
                  </Grid>
                  {recordingDetails.isRemuxed ? (
                    <StreamingAudioPlayer
                      ref={streamingAudioPlayerRef}
                      recordingId={recordingDetails.id}
                      durationMs={recordingDetails.durationMs}
                    />
                  ) : (
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      Audio can be played once fully processed.
                    </Text>
                  )}
                </Stack>
              </Paper>

              {
                <Paper shadow="xs" withBorder radius="md" mt={20} p={15}>
                  <Stack gap="sm">
                    <Group justify="space-between" align="flex-start">
                      <Title mt={0} mb={0} order={5}>
                        Notes on Recording
                      </Title>
                      <Stack gap={2} align="flex-end">
                        <Checkbox
                          size="sm"
                          label="Include note in Living Story"
                          checked={includeNotesInLivingStory}
                          disabled={
                            isEditorEmpty || !hasEditLivingStoryPermission
                          }
                          onChange={(event) =>
                            setIncludeNotesInLivingStory(
                              event.currentTarget.checked
                            )
                          }
                        />
                        {includeNotesInLivingStory && isEditorEmpty && (
                          <Text size="xs" c="red">
                            Cannot include empty note on Living Story
                          </Text>
                        )}
                      </Stack>
                    </Group>

                    <RichTextEditor editor={editor}>
                      <RichTextEditor.Toolbar sticky stickyOffset={60}>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Bold />
                          <RichTextEditor.Italic />
                          <RichTextEditor.Underline />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.BulletList />
                          <RichTextEditor.OrderedList />
                        </RichTextEditor.ControlsGroup>

                        <Box visibleFrom="lg">
                          <RichTextEditor.ControlsGroup>
                            <RichTextEditor.H1 />
                            <RichTextEditor.H2 />
                            <RichTextEditor.H3 />
                            <RichTextEditor.H4 />
                          </RichTextEditor.ControlsGroup>
                        </Box>

                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.ClearFormatting />
                        </RichTextEditor.ControlsGroup>
                      </RichTextEditor.Toolbar>

                      <RichTextEditor.Content
                        style={{ fontSize: 15, minHeight: 120 }}
                      />
                    </RichTextEditor>

                    <Group justify="flex-end">
                      <Tooltip
                        disabled={hasNotesPermission}
                        label={
                          hasNotesPermission
                            ? undefined
                            : "You do not have access to write notes on a recording."
                        }
                      >
                        <Button
                          onClick={handleSaveNotes}
                          loading={updateNotesMutation.isPending}
                          disabled={
                            (includeNotesInLivingStory && isEditorEmpty) ||
                            !hasNotesPermission
                          }
                        >
                          Save Notes
                        </Button>
                      </Tooltip>
                    </Group>
                  </Stack>
                </Paper>
              }
            </Grid.Col>
            <Grid.Col span={7}>
              <RoutedTabs
                route={Route}
                tabs={[
                  { value: "summary", label: "Recording Summary" },
                  { value: "transcript", label: "Recording Transcript" }
                ]}
              />
              <Box h="60vh" ml={8} my={10}>
                <RecordingContext value={recordingContext}>
                  <Outlet />
                </RecordingContext>
              </Box>
            </Grid.Col>
          </Grid>
        )}
      </Modal>

      <UnsavedChangesModal
        opened={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        onDiscard={() => {
          isDiscardUnsavedChangesConfirmed.current = true;
          handleClose();
        }}
      />
    </>
  );
}
